import React, { createContext, useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { FilePreviewContextType, FilePreviewProviderProps, FilePreviewState } from '../types';
import { detectFileType, isValidHttpUrl } from '../utils/fileTypeDetection';
import { viewerRegistry } from '../registry/ViewerRegistry';

export const FilePreviewContext = createContext<FilePreviewContextType | undefined>(undefined);

export const FilePreviewProvider: React.FC<FilePreviewProviderProps> = ({ children }) => {
  const [state, setState] = useState<FilePreviewState>({
    isOpen: false,
    fileUrl: null,
    fileType: null,
    fileName: null,
  });

  // Track blob URLs for cleanup
  const blobUrlsRef = useRef<Set<string>>(new Set());

  /**
   * Cleanup function for blob URLs
   */
  const cleanupBlobUrl = useCallback((url: string | null) => {
    if (url && url.startsWith('blob:') && blobUrlsRef.current.has(url)) {
      URL.revokeObjectURL(url);
      blobUrlsRef.current.delete(url);
    }
  }, []);

  /**
   * Cleanup all blob URLs on unmount
   */
  useEffect(() => {
    return () => {
      blobUrlsRef.current.forEach(url => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []);

  /**
   * Opens the file preview
   */
  const open = useCallback((url: string, fileName?: string) => {
    if (!url) {
      console.warn('FilePreview: Cannot open with empty URL');
      return;
    }

    if (!isValidHttpUrl(url)) {
      console.warn('FilePreview: URL must be HTTP/HTTPS or blob:', url);
      return;
    }

    // For blob URLs, use fileName for type detection; otherwise use URL
    const detectionSource = url.startsWith('blob:') && fileName ? fileName : url;
    const fileType = detectFileType(detectionSource);

    // Track blob URL if applicable
    if (url.startsWith('blob:')) {
      blobUrlsRef.current.add(url);
    }

    setState({
      isOpen: true,
      fileUrl: url,
      fileType,
      fileName: fileName || null,
    });
  }, []);

  /**
   * Closes the file preview
   */
  const close = useCallback(() => {
    // Cleanup blob URL if present
    cleanupBlobUrl(state.fileUrl);

    setState({
      isOpen: false,
      fileUrl: null,
      fileType: null,
      fileName: null,
    });
  }, [state.fileUrl, cleanupBlobUrl]);

  /**
   * Sets the file URL without opening
   */
  const setFileUrl = useCallback((url: string | null) => {
    if (url && !isValidHttpUrl(url)) {
      console.warn('FilePreview: URL must be HTTP/HTTPS:', url);
      return;
    }

    const fileType = url ? detectFileType(url) : null;

    setState(prev => ({
      ...prev,
      fileUrl: url,
      fileType,
    }));
  }, []);

  /**
   * Checks if a file type is supported
   */
  const isSupported = useCallback((url: string): boolean => {
    if (!url || !isValidHttpUrl(url)) return false;

    const fileType = detectFileType(url);
    return viewerRegistry.isSupported(url, fileType);
  }, []);

  const value: FilePreviewContextType = useMemo(
    () => ({
      ...state,
      open,
      close,
      setFileUrl,
      isSupported,
    }),
    [state, open, close, setFileUrl, isSupported]
  );

  return (
    <FilePreviewContext.Provider value={value}>
      {children}
    </FilePreviewContext.Provider>
  );
};

export default FilePreviewContext;
